import * as classNames from 'classnames'
import * as React from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'
import { useTable, usePagination, Column, useSortBy } from 'react-table'
import { IDriverFilters, IDriverSearchObject } from '../../types'
import Edit from '../icons/ui/Edit'
import Archive from '../icons/drivers/Archive'
import RestoreFromArchive from '../icons/drivers/RestoreFromArchive'
import ReactTablePagination from '../ui/ReactTablePagination'
import ContextMenu from '../ui/ContextMenu'
import { drivers } from 'src/state/reducers/drivers'
import { maskPhoneNumber } from '../../utils/mask'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { getAccessJwtPayload } from '../../utils/auth'
import { access } from 'fs'
import { MoonLoader } from 'react-spinners'

export interface ITableDriversProps {
  filters: IDriverFilters
  drivers: IDriverSearchObject[]
  currentOrganizationId: string
  isFetching: boolean
  error: string
  ownProps: RouteComponentProps<any>
  showDrivers: string
  getDriversList: (filters: IDriverFilters) => Promise<any>
  restoreDriver: (item: IDriverSearchObject) => Promise<any>
  archiveDriver: (item: IDriverSearchObject) => Promise<any>
  updateDriverFilters: (item: IDriverFilters) => Promise<any>
  typeDrivers: string
  totalDrivers: number
}

export interface ITableDriversState {
  isArchived: boolean
  search: string
  pages: number
}

interface IObjectLink {
  linkTo: string
  linkName: string
}

interface IFetchDataParams {
  filters: IDriverFilters
  pageSize: number
  pageIndex: number
}
interface IProps<T extends object> {
  organizationId: string
  columns: Array<Column<T>>
  filters: IDriverFilters
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { filters: IDriverFilters; pageIndex: number; pageSize: number }) => void
  updateFilters: (params: IDriverFilters) => Promise<any>
  pageSize: number
  setPageSize: (size: number) => any
  pageIndex: number
  setPageIndex: (size: number) => any
}

const PAGE_SIZE = 10

function Table<T extends object>({
  organizationId,
  columns,
  data,
  fetchData,
  updateFilters,
  filters,
  loading,
  pageCount: controlledPageCount,
  pageSize,
  setPageSize,
  pageIndex,
  setPageIndex
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    state
  } = useTable<T>(
    {
      columns,
      data,
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { sortBy } = state

  React.useEffect(() => {
    if (filters.organizationId === null || filters.organizationId === undefined) {
      filters.organizationId = organizationId
    }
    fetchData({ filters, pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])

  React.useEffect(() => {
    setPageIndex(0)
  }, [pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'DESC' : 'ASC')
    })
    if (sortParams) {
      updateFilters({ sortBy: sortParams })
    }
  }, [sortBy])

  const nextPage = () => {
    setPageIndex(pageIndex + 1)
  }

  const previousPage = () => {
    setPageIndex(pageIndex - 1)
  }

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex flex-column justify-center items-center' style={{ width: '90%', height: '40vh' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          <span className='pt1'>Loading...</span>
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(
              row =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()} key={`driverColumn-${cell.row.index}-${cell.column.id}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )}
          </tbody>
        </table>
      )}

      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const CellContextMenu = (props: {
  driver: IDriverSearchObject
  toggleArchive: (item: IDriverSearchObject) => () => void
}) => {
  const archiveText = props.driver.isArchived ? 'Restore from archive' : 'Move to archive'
  const archiveIcon = props.driver.isArchived ? (
    <RestoreFromArchive className='icon--16x16 cursor--pointer' />
  ) : (
    <Archive className='icon--16x16 cursor--pointer' />
  )
  return (
    <div className='flex items-center'>
      <ContextMenu>
        <div className='flex items-center p02 cursor--pointer'>
          <Edit className='svg-grey2 icon--16x16 cursor--pointer' />
          <Link to={`/drivers/${props.driver.id}/edit`}>
            <span className='ml1 font-dark-grey h5'>Edit</span>
          </Link>
        </div>
        <div className='flex items-center p02 cursor--pointer'>
          <Edit className='svg-grey2 icon--16x16 cursor--pointer' />
          <Link to={`/drivers/${props.driver.id}/assign-vehicle`}>
            <span className='ml1 font-dark-grey h5'>Assign Vehicle</span>
          </Link>
        </div>
        <div className='flex items-center p02 cursor--pointer' onClick={props.toggleArchive(props.driver)}>
          {archiveIcon}
          <span className='ml1 font-dark-grey h5'>{archiveText}</span>
        </div>
      </ContextMenu>
    </div>
  )
}

export default function TableDrivers(props: ITableDriversProps) {
  const [loading, setLoading] = React.useState(false)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const [pageIndex, setPageIndex] = React.useState(0)
  const [totalItems, setTotalItems] = React.useState(0)
  const [pageCount, setPageCount] = React.useState(1)
  const accessJwtPayload = getAccessJwtPayload()

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        width: 200,
        accessor: (driver: IDriverSearchObject) => {
          return <Link to={`/drivers/${driver.id}/details`}>{driver.name}</Link>
        }
      },
      {
        id: 'phone',
        Header: 'Phone number',
        disableSortBy: true,
        accessor: (driver: IDriverSearchObject) => {
          return maskPhoneNumber(driver.phone)
        },
        className: 'flex items-center'
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (driver: IDriverSearchObject) => {
          return <Link to={`/drivers/${driver.id}/details`}>{driver.email}</Link>
        },
        className: 'flex items-center'
      },
      {
        id: 'products',
        Header: 'Products',
        disableSortBy: true,
        accessor: (driver: IDriverSearchObject) => {
          let temp = ''
          const links = Array()

          if (driver.products) {
            driver.products.map((p, i) => {
              const newLink = {
                linkTo: `/products/${driver.productIds[i]}`,
                linkName: p.name
              }
              links.push(newLink)
            })

            links.map(link => {
              temp += link.linkName + ', '
            })

            temp = temp.slice(0, -2)
          }
          return <Link to={`/drivers/${driver.id}/products`}>{temp}</Link>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicle_name',
        Header: 'Vehicle',
        disableSortBy: true,
        accessor: (driver: IDriverSearchObject) => {
          if (driver.vehicle) {
            return (
              <Link to={`/drivers/${driver.id}/details`}>
                {driver.vehicle.model} {driver.vehicle.make}
                <br />
                {driver.vehicle.licensePlate}
              </Link>
            )
          } else {
            return <div />
          }
        },
        className: 'flex items-center'
      },
      {
        id: 'context-menu',
        Header: '',
        Cell: (data: any) => {
          const toggleArchive = data.row.original.isArchived ? restoreDriver : archiveDriver
          return <CellContextMenu driver={data.row.original} toggleArchive={toggleArchive} />
        }
      }
    ],
    []
  )

  React.useEffect(() => {
    props.updateDriverFilters({
      organizationId: props.currentOrganizationId
    })
  }, [])

  React.useEffect(() => {
    setPageCount(Math.ceil(totalItems / pageSize))
  }, [totalItems, pageSize])

  const archiveDriver = (item: IDriverSearchObject) => async () => {
    await props.archiveDriver(item)
  }

  const restoreDriver = (item: IDriverSearchObject) => async () => {
    await props.restoreDriver(item)
  }

  const fetchData = async ({ filters, pageSize, pageIndex }: IFetchDataParams) => {
    setLoading(true)
    const response = await props.getDriversList({ ...filters, skip: pageIndex * pageSize, take: pageSize })
    setLoading(false)
    setTotalItems(response.total)
    setPageSize(pageSize)
  }

  const organizationIds =
    accessJwtPayload && accessJwtPayload.rolesInOrganizations
      ? accessJwtPayload.rolesInOrganizations.map(role => role.organizationId)
      : []
  const org = organizationIds.join(',')
  const orgArray = org ? org.split(',') : []

  // Filter drivers based on organization ID and user membership status
  let filteredDrivers: IDriverSearchObject[]
  if (orgArray.length === 1) {
    // If there is only one organization ID
    filteredDrivers = props.drivers.filter(driver => {
      // Check if the driver's organization ID matches
      return driver.organizationIds.includes(org)
    })
  } else if (orgArray.length > 1) {
    // If there are multiple organization IDs
    filteredDrivers = props.drivers.filter(driver => {
      // Check if the driver's organization IDs include any of the user's organization IDs
      return driver.organizationIds.some(driverOrgId => orgArray.includes(driverOrgId))
    })
  } else {
    // If there are no organization IDs, show all drivers
    filteredDrivers = props.drivers
  }

  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          organizationId={props.currentOrganizationId}
          columns={columns}
          data={filteredDrivers || []} //props.drivers || []
          fetchData={fetchData}
          loading={loading}
          filters={props.filters}
          pageCount={pageCount}
          updateFilters={props.updateDriverFilters}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/drivers/TableDrivers.tsx